import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getDebouncedFieldUpdate } from '../../../utils/debouncedApiCalls';
import { AuthContext } from '../../../contexts/AuthContext';
import { initialGroup } from '../models';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import api from '../../../api';
import { ErrorContext } from './ErrorContext';

// Add isTest detection for better testing behavior
const isTestEnvironment = () => process.env.NODE_ENV === 'test';

/**
 * Core context for Group data and basic navigation
 */
export const GroupCoreContext = createContext();

/**
 * Provider for core Group data and navigation
 */
const CACHE_KEY = 'groupIntakeFormData';

export const GroupCoreProvider = ({ children }) => {
  const { auth } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // State for steps and navigation
  const [currentStep, setCurrentStep] = useState(0);
  const [group, setGroup] = useState(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? { ...initialGroup, ...JSON.parse(cached) } : { ...initialGroup };
    } catch {
      return { ...initialGroup };
    }
  });
  const [initialCacheLoaded, setInitialCacheLoaded] = useState(() => {
    try {
      return !!localStorage.getItem(CACHE_KEY);
    } catch {
      return false;
    }
  });
  const [existingGroupId, setExistingGroupId] = useState(null);
  const [hasLoadedInitialGroup, setHasLoadedInitialGroup] = useState(false);
  
  // State for form processing and feedback
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Error and loading states
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const BASE = process.env.REACT_APP_RXROOSTER_BACKEND_URL;

  // Persist group data to cache whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(group));
    } catch (err) {
      console.warn('Failed to cache group intake data:', err);
    }
  }, [group]);
  // Fetch user profile to get existing group ID when component mounts
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (auth?.token) {
        try {
          const response = await fetch(`${BASE}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${auth.token}`
            }
          });

          if (response.ok) {
            const userData = await response.json();
            
            // If user has a group, update the existingGroupId and the group state
            if (userData.group && userData.group._id) {
              const groupId = userData.group._id;
              
              // Store the existing group ID so we know to update not create
              setExistingGroupId(groupId);
              
              // Update the group state with the existing group data
              setGroup(prevGroup => ({
                ...prevGroup,
                id: groupId,
                _id: groupId,
                name: userData.group.name || prevGroup.name,
                description: userData.group.description || prevGroup.description,
                logoUrl: userData.group.logoUrl || prevGroup.logoUrl,
                website: userData.group.website || prevGroup.website,
                contact: userData.group.contact || prevGroup.contact
              }));

              // Mark that we've loaded the initial group data
              setHasLoadedInitialGroup(true);
              setInitialCacheLoaded(true);
            }
          } else {
            console.error('Failed to fetch user profile:', await response.text());
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };
    
    // Only fetch if we haven't already loaded the initial group
    if (!hasLoadedInitialGroup) {
      fetchUserProfile();
    }
  }, [auth?.token, hasLoadedInitialGroup]);
  
  // Group-related mutations
  const { mutate: patchGroup, isLoading: isPatchingGroup } = useMutation({
    mutationFn: ({ id, data }) => api.put(`/groups/${id}/intake`, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['group', variables.id]);
    }
  });
  
  const { mutate: createFullGroup, isLoading: isCreatingGroup } = useMutation({
    mutationFn: ({ data }) => api.post('/groups/intake', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['auth', 'me']);
    }
  });
  
  const { mutate: updateFullGroupIntake, isLoading: isUpdatingGroupIntake } = useMutation({
    mutationFn: ({ id, data }) => api.put(`/groups/${id}/intake`, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['group', variables.id]);
    }
  });
  
  // Get current user data to find their group
  const { 
    data: userData, 
    isLoading: isLoadingUser
  } = useQuery({  
    queryKey: ['auth', 'me'],
    queryFn: () => api.get('/auth/me'),
    enabled: !!auth?.token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      console.error('Error fetching user data:', error);
      setErrors(prev => ({ 
        ...prev, 
        auth: 'Failed to load user data. Please check your authentication status.'
      }));
    }
  });
  
  // Extract group ID from user data
  const groupId = userData?.group?._id || userData?.group?.id || existingGroupId;
  
  // Fetch full group data with facilities
  const { 
    isLoading: isLoadingGroup,
    refetch: refetchGroup
  } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => groupId ? api.get(`/groups/${groupId}`) : null,
    enabled: !!groupId && !!auth?.token && !hasLoadedInitialGroup,
    staleTime: 2 * 60 * 1000, // 2 minutes
    onSuccess: (response) => {
      if (response && response.data) {
        setGroup(response.data);
        setExistingGroupId(groupId);
        setHasLoadedInitialGroup(true);
        setInitialCacheLoaded(true);
      }
    },
    onError: (error) => {
      console.error('Error fetching group data:', error);
      setErrors(prev => ({ 
        ...prev, 
        group: 'Failed to load group data. Please try refreshing the page.'
      }));
      // Try localStorage as fallback
      loadFromLocalStorage();
    }
  });
  
  // Backup to localStorage as a fallback
  const saveToLocalStorage = useCallback(() => {
    if (!existingGroupId) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(group));
      return true;
    }
    return false;
  }, [group, existingGroupId]);

  // Load backup from localStorage if available
  const loadFromLocalStorage = useCallback(() => {
    if (!existingGroupId) {
      const savedGroup = localStorage.getItem(CACHE_KEY);
      if (savedGroup) {
        setGroup(JSON.parse(savedGroup));
        setInitialCacheLoaded(true);
      }
      return true;
    }
    return false;
  }, [existingGroupId]);
  
    const updateGroupField = useCallback((field, value) => {
    if (!auth?.token) {
      console.warn('Cannot update group field: No authentication token found');
      setErrors(prev => ({ ...prev, auth: 'Authentication required to update group data' }));
      return;
    }

    // 1) Compute the NEW group object (what your state *will* be)
    const updatedGroup = (() => {
      const copy = { ...group };
      if (field.includes('.')) {
        const parts = field.split('.');
        let cur = copy;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!cur[parts[i]]) cur[parts[i]] = {};
          cur = cur[parts[i]];
        }
        cur[parts[parts.length - 1]] = value;
      } else {
        copy[field] = value;
      }
      return copy;
    })();

    // 2) Update the local state immediately for UI responsiveness
    setGroup(updatedGroup);


    // 3) If we've already created this group on the server, PATCH only the changed field:
    if (existingGroupId) {
      if (!isTestEnvironment()) {

        const actualApiCallLogic = (/* latestValueForField from debouncer is not directly used here */) => {
          // We use the 'updatedGroup' from the outer scope of updateGroupField,
          // which contains the complete group data with the most recent update.
          const payload = {
            group: { ...updatedGroup }, // Send a copy of the complete updated group data
            facilities: updatedGroup.facilities || [] // Ensure facilities array is present, even if empty
          };
          // Remove backend-generated IDs from the payload.group to prevent errors
          delete payload.group._id; 
          delete payload.group.id; // if 'id' is a frontend alias for '_id' or another internal ID

          patchGroup({
            id: existingGroupId,
            data: payload, // Send the structured payload
          });
        };

        const debouncedUpdaterForField = getDebouncedFieldUpdate(
          existingGroupId,    // entityKey: the group ID
          field,              // fieldPath: the name of the field being updated
          actualApiCallLogic, // apiCallFn: the function to execute
          5000                // delay (e.g., 5 seconds, adjust as needed)
        );

        debouncedUpdaterForField(value);

      } else {
        // Fallback for tests or if no group ID. Consider if saveToLocalStorage() is appropriate here.
        // saveToLocalStorage();
      }
      return; // Return after handling the existing group case
    }

    // 4) Otherwise we're still in "new group" land, so debounce a create
    // (and only fire it once we actually have a group.name — you can
    // loosen or tighten this guard however you like)
    if (!updatedGroup.name) {
      // don't create until the user has typed in a real name
      return;
    }

    const debouncedGroupCreate = getDebouncedFieldUpdate(
      'new-group-creation',
      'create',
      () => {

        const completeIntakeData = {
          group: {
            name:        updatedGroup.name,
            description: updatedGroup.description || '',
            logoUrl:     updatedGroup.logoUrl || '',
            website:     updatedGroup.website || '',
            contact: {
              name:  updatedGroup.contact?.name  || '',
              email: updatedGroup.contact?.email || '',
              phone: updatedGroup.contact?.phone || ''
            }
          },
          facilities: []
        };

        return createFullGroup(
          { data: completeIntakeData },
          {
            onSuccess: (data) => {
              setExistingGroupId(data.id || data._id);
            },
            onError: (err) => {
              console.error('❌ Failed to create group:', err);
              setErrors(prev => ({
                ...prev,
                creation: 'Failed to create new group. Changes have been saved locally.'
              }));
            }
          }
        );
      },
      1200
    );

    debouncedGroupCreate();
  }, [
    auth?.token,
    existingGroupId,
    group,               // (we only use it to compute updatedGroup)
    createFullGroup,
    patchGroup, // Added patchGroup as a dependency
    setErrors
  ]);


  // Submit the complete form
  const submitGroupIntake = useCallback(async (formData) => {
    if (!auth?.token) {
      setErrors(prev => ({ ...prev, auth: 'Authentication required to submit form' }));
      return { success: false, error: 'Authentication required' };
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      let result;
      
      // Structure the data according to CompleteGroupIntakeDto with explicit values
      // This prevents issues with object references or state synchronization in production builds
      const completeIntakeData = {
        group: {
          name: group.name || '',
          description: group.description || '',
          logoUrl: group.logoUrl || '',
          website: group.website || '',
          contact: {
            name: group.contact?.name || '',
            email: group.contact?.email || '',
            phone: group.contact?.phone || ''
          }
        },
        facilities: formData.facilities || []
      };
      

      
      if (existingGroupId) {
        // Update existing group - ensure the correct payload structure
        result = await updateFullGroupIntake(
          { id: existingGroupId, data: completeIntakeData },
          {
            onSuccess: () => {
              setSubmitSuccess(true);
              // Clear localStorage since we've successfully saved
              localStorage.removeItem(CACHE_KEY);
              localStorage.removeItem('facilitiesForm');
            },
            onError: (error) => {
              console.error('Error updating group intake:', error);
              setErrors(prev => ({ 
                ...prev, 
                submission: 'Failed to update group intake. Please check your data and try again.' 
              }));
            }
          }
        );
      } else {
        // Create new group with the correct CompleteGroupIntakeDto structure
        result = await createFullGroup(
          { data: completeIntakeData },  // Use the properly structured data
          {
            onSuccess: (data) => {
              setSubmitSuccess(true);
              setExistingGroupId(data.id || data._id);
              // Clear localStorage since we've successfully saved
              localStorage.removeItem(CACHE_KEY);
              localStorage.removeItem('facilitiesForm');
            },
            onError: (error) => {
              console.error('Error creating group intake:', error);
              setErrors(prev => ({ 
                ...prev, 
                submission: 'Failed to create group intake. Please check your data and try again.' 
              }));
            }
          }
        );
      }
      
      return { success: true, data: result };
    } catch (error) {
      console.error('Error in group intake submission:', error);
      setErrors(prev => ({ 
        ...prev, 
        submission: 'Unexpected error during submission. Please try again later.' 
      }));
      return { success: false, error: error.message || 'Unknown error' };
    } finally {
      setIsSubmitting(false);
    }
  }, [auth?.token, existingGroupId, updateFullGroupIntake, createFullGroup, group, group.name, group.description, group.logoUrl, group.website, group.contact]);
  
  // Navigate to a specific step
  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);
  
  // Check if we're currently loading
  const isLoading = (!initialCacheLoaded && (isLoadingUser || isLoadingGroup)) ||
    isPatchingGroup || isCreatingGroup || isUpdatingGroupIntake;
  
  // Context value
  const contextValue = {
    currentStep,
    goToStep,
    group,
    updateGroupField,
    existingGroupId,
    errors,
    setErrors,
    isLoading,
    isSubmitting,
    submitSuccess,
    submitGroupIntake,
    groupId,
    loadFromLocalStorage,
    saveToLocalStorage,
    hasLoadedInitialGroup,
    refetchGroup
  };
  
  return (
    <GroupCoreContext.Provider value={contextValue}>
      {children}
    </GroupCoreContext.Provider>
  );
};

export const useGroupCore = () => useContext(GroupCoreContext);
